import { convertAvailabilityScheduledAtWithTimezone } from '@/administration/pages/Journey/common/utils/convertAvailabilityScheduledAtWithTimezone';
import { useCurrentUser } from '@/store/currentUser';
import { useLearningJourneySectionQueryRemote, useLearningJourneyUpcomingItemQueryRemote } from '@/store/v2';
import { LearningJourneyItem } from './journey';
import { useLearningJourney } from './useLearningJourney';
import { useLearningJourneyBreadcrumbs } from './useLearningJourneyBreadcrumbs';

export const useJourneyEnhancedNavigation = ({
  id,
  isProduct,
}: {
  id: number;
  isProduct?: boolean;
}): {
  currentItem: LearningJourneyItem | null;
  nextItem: LearningJourneyItem | null;
  journeyId?: number;
  journeyTitle?: string;
  allUpcomingItemsLocked: boolean;
  allUpcomingItemsUnlockAt?: Date | string;
} => {
  const { user } = useCurrentUser();
  const learningJourneyBreadcrumbs = useLearningJourneyBreadcrumbs({ id, isProduct });

  const { items, journey } = useLearningJourney({
    id: Number(learningJourneyBreadcrumbs?.journeyId),
    include: {
      items: true,
    },
    skip: !learningJourneyBreadcrumbs?.journeyId,
    itemsFilter: user?.realm ? { realmVisibility: user.realm } : undefined,
  });

  const sectionId = learningJourneyBreadcrumbs?.parentSectionId;

  const { data: learningJourneySection, loading: learningJourneySectionLoading } = useLearningJourneySectionQueryRemote({
    variables: {
      id: Number(sectionId),
      filter: {
        realmVisibility: user?.realm,
      },
      pagination: {
        limit: -1,
        page: 1,
      },
      userId: user?.id ?? 0,
      spaceIds: [journey?.spaceId ?? 0],
      includeContentProgress: true,
      includeSectionProgress: false,
      includeUpdateByUser: false,
      includeIsLocked: true,
    },
    skip: !sectionId || !journey?.id || !user?.id || !journey?.spaceId,
    fetchPolicy: 'network-only',
  });

  const { data: nextItemData } = useLearningJourneyUpcomingItemQueryRemote({
    variables: {
      journeyId: Number(journey?.id),
      spaceIds: [journey?.spaceId ?? 0],
      userId: user?.id ?? 0,
      includeContentProgress: false,
      afterItemURN: learningJourneyBreadcrumbs?.currentLearningObjectId as string,
    },
    fetchPolicy: 'network-only',
    skip:
      !journey?.id ||
      !journey?.spaceId ||
      !user?.id ||
      !learningJourneyBreadcrumbs?.currentLearningObjectId ||
      (Boolean(sectionId) && learningJourneySectionLoading),
  });

  const targetItem = items?.find((item) => item.contentId === learningJourneyBreadcrumbs?.currentLearningObjectId) || null;

  const next = nextItemData?.learningJourneyUpcomingItem;

  const sortedItems = items?.slice()?.sort((a, b) => a.order - b.order);
  const allUpcomingItemsUnlockAtFromJourney =
    sortedItems?.find((item) => item.isLocked && !item.isOptional && item.availabilityScheduledAt)?.availabilityScheduledAt ||
    sortedItems?.find((item) => item.isLocked && item.availabilityScheduledAt)?.availabilityScheduledAt;

  const sectionItemsData = learningJourneySection?.learningV2?.sectionItems?.data;
  const sortedSectionItems = sectionItemsData?.slice()?.sort((a, b) => a.order - b.order);
  const firstIncompleteRequiredSectionItem = sortedSectionItems
    ?.filter((item) => !item.isOptional)
    .find((item) => !item.learningItem?.progress?.isCompleted);

  const allUpcomingItemsUnlockAtFromSection =
    firstIncompleteRequiredSectionItem?.isLocked && firstIncompleteRequiredSectionItem.availabilityScheduledAt
      ? convertAvailabilityScheduledAtWithTimezone({
          availabilityScheduledAt: firstIncompleteRequiredSectionItem.availabilityScheduledAt,
          availabilityScheduledAtTimezone: firstIncompleteRequiredSectionItem.availabilityScheduledAtTimezone,
          direction: 'fromCustomTimezone',
        })
      : undefined;

  // Match JourneyView: derive unlock time only from section + top-level journey items — not from
  // learningJourneyUpcomingItem.availabilityScheduledAt (that field converts differently and showed
  // wrong short countdowns on learning/course pages vs the journey screen).
  const allUpcomingItemsUnlockAt =
    sectionId && sectionItemsData?.length
      ? (allUpcomingItemsUnlockAtFromSection ?? allUpcomingItemsUnlockAtFromJourney)
      : allUpcomingItemsUnlockAtFromJourney;

  const nextRequiredUpcomingIsLocked = Boolean(next?.isLocked) && !next?.isOptional;
  const sectionAllUpcomingLocked = Boolean(sectionId) && Boolean(firstIncompleteRequiredSectionItem?.isLocked);

  const allUpcomingItemsLocked =
    Boolean(journey?.allUpcomingItemsLocked) || nextRequiredUpcomingIsLocked || sectionAllUpcomingLocked;

  return {
    journeyId: learningJourneyBreadcrumbs?.journeyId,
    journeyTitle: learningJourneyBreadcrumbs?.journeyTitle,
    nextItem: (next as LearningJourneyItem) || null,
    currentItem: targetItem,
    allUpcomingItemsLocked,
    allUpcomingItemsUnlockAt,
  };
};
